// shared/api/journeys.js
// Journey API - All journey-related functions
import { supabase } from '../supabase';

/**
 * Fetch all active journeys
 * @returns {Promise<{journeys, error}>}
 */
export const fetchActiveJourneys = async () => {
  try {
    const { data, error } = await supabase
      .from('journeys')
      .select('*')
      .eq('active', true)
      .order('order_index');

    if (error) throw error;
    return { journeys: data || [], error: null };
  } catch (error) {
    console.error('Error fetching journeys:', error);
    return { journeys: [], error };
  }
};

/**
 * Fetch all journeys (active and inactive)
 * @returns {Promise<{journeys, error}>}
 */
export const fetchAllJourneys = async () => {
  try {
    const { data, error } = await supabase
      .from('journeys')
      .select('*')
      .order('order_index');

    if (error) throw error;
    return { journeys: data || [], error: null };
  } catch (error) {
    console.error('Error fetching all journeys:', error);
    return { journeys: [], error };
  }
};

/**
 * Fetch single journey by ID
 * @param {string} journeyId 
 * @returns {Promise<{journey, error}>}
 */
export const fetchJourneyById = async (journeyId) => {
  try {
    const { data, error } = await supabase
      .from('journeys')
      .select('*')
      .eq('id', journeyId)
      .single();

    if (error) throw error;
    return { journey: data, error: null };
  } catch (error) {
    console.error('Error fetching journey:', error);
    return { journey: null, error };
  }
};

/**
 * Fetch premium journeys (requires premium access)
 * @returns {Promise<{journeys, error}>}
 */
export const fetchPremiumJourneys = async () => {
  try {
    const { data, error } = await supabase
      .from('journeys')
      .select('*')
      .eq('is_premium', true)
      .eq('active', true)
      .order('order_index');

    if (error) throw error;
    return { journeys: data || [], error: null };
  } catch (error) {
    console.error('Error fetching premium journeys:', error);
    return { journeys: [], error };
  }
};

/**
 * Create a new journey (Admin only)
 * @param {Object} journeyData 
 * @returns {Promise<{journey, error}>}
 */
export const createJourney = async (journeyData) => {
  try {
    const { data, error } = await supabase
      .from('journeys')
      .insert(journeyData)
      .select()
      .single();

    if (error) throw error;
    return { journey: data, error: null };
  } catch (error) {
    console.error('Error creating journey:', error);
    return { journey: null, error };
  }
};

/**
 * Update existing journey (Admin only)
 * @param {string} journeyId 
 * @param {Object} updates 
 * @returns {Promise<{journey, error}>}
 */
export const updateJourney = async (journeyId, updates) => {
  try {
    const { data, error } = await supabase
      .from('journeys')
      .update(updates)
      .eq('id', journeyId)
      .select()
      .single();

    if (error) throw error;
    return { journey: data, error: null };
  } catch (error) {
    console.error('Error updating journey:', error);
    return { journey: null, error };
  }
};

/**
 * Delete journey (Admin only)
 * @param {string} journeyId 
 * @returns {Promise<{success, error}>}
 */
export const deleteJourney = async (journeyId) => {
  try {
    const { error } = await supabase
      .from('journeys')
      .delete()
      .eq('id', journeyId);

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting journey:', error);
    return { success: false, error };
  }
};

/**
 * Fetch journeys by category
 * @param {string} category 
 * @returns {Promise<{journeys, error}>}
 */
export const fetchJourneysByCategory = async (category) => {
  try {
    const { data, error } = await supabase
      .from('journeys')
      .select('*')
      .eq('category', category)
      .eq('active', true)
      .order('order_index');

    if (error) throw error;
    return { journeys: data || [], error: null };
  } catch (error) {
    console.error('Error fetching journeys by category:', error);
    return { journeys: [], error };
  }
};
